import { io, type Socket } from 'socket.io-client';
import { TYPING_THROTTLE_MS, type ClientToServerEvents, type MessageTargetRef, type ServerToClientEvents } from '@clanfire/shared';
import { useConnection } from '@/stores/realtime';
import { bindHandlers, resync } from './handlers';
import { voiceManager } from './voice';

export type ClanFireSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: ClanFireSocket | null = null;
let hasConnected = false;
let onUnauthorized: (() => void) | null = null;

/** Registered by the app shell: what to do if the socket reports the session is gone. */
export function setSocketUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export function connectSocket(): ClanFireSocket {
  if (socket) return socket;
  const status = useConnection.getState();
  status.setStatus('connecting');

  const s: ClanFireSocket = io({
    path: '/socket.io',
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnectionDelay: 800,
    reconnectionDelayMax: 8000,
  });
  socket = s;

  s.on('connect', () => {
    useConnection.getState().setStatus('connected');
    if (hasConnected) {
      void resync();
      voiceManager.onReconnect();
    }
    hasConnected = true;
  });

  s.on('disconnect', (reason) => {
    if (reason === 'io client disconnect') {
      useConnection.getState().setStatus('offline');
      return;
    }
    useConnection.getState().setStatus('reconnecting');
    // The server closed the connection (e.g. a deploy): reconnect manually.
    if (reason === 'io server disconnect') setTimeout(() => socket === s && s.connect(), 1500);
  });

  s.on('connect_error', (err) => {
    if (err.message === 'unauthorized') {
      onUnauthorized?.();
      return;
    }
    useConnection.getState().setStatus('reconnecting');
  });

  bindHandlers(s);
  return s;
}

export function disconnectSocket() {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
  hasConnected = false;
}

export function getSocket(): ClanFireSocket | null {
  return socket;
}

const lastTyping = new Map<string, number>();

/** Emits a typing signal at most once per TYPING_THROTTLE_MS per target. */
export function emitTyping(target: MessageTargetRef) {
  const key = target.channelId ?? target.conversationId ?? '';
  const now = Date.now();
  if (now - (lastTyping.get(key) ?? 0) < TYPING_THROTTLE_MS) return;
  lastTyping.set(key, now);
  socket?.emit('typing:start', target.channelId ? { channelId: target.channelId } : { conversationId: target.conversationId });
}

export function resetTypingThrottle(target: MessageTargetRef) {
  lastTyping.delete(target.channelId ?? target.conversationId ?? '');
}
